import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ParamSerializer } from 'src/app/shared';
import type { PagedResponse } from 'src/app/shared';
import { environment } from 'src/environments/environment';
import type { Overlay, OverlayPoint } from './overlay';
import type { OverlayQueryOpts } from './overlay-query-opts';

/**
 * Service for controlling overlay data
 */
@Injectable()
export class OverlayService {

  /** API Url */
  private readonly apiUrl = `${environment.apiBaseUrl}/avl/overlays`;

  /**
   * Clears cache on login and logout
   */
  public constructor(@Inject(HttpClient) private readonly http: HttpClient,
    @Inject(ParamSerializer) private readonly paramSerializer: ParamSerializer) {}

  /**
   * Gets list of overlays for dropdown options
   * @param flushCache Takes boolean to determine whether to clear dropdown options
   * @returns Overlay dropdownoptions
   */
  public getOptions(): Observable<Overlay[]> {
    return this.search({ limit: 10000 }).pipe(map(x => x.data));
  }

  /**
   * Creates new Overlay
   * @param overlay Takes Overlay as object to create
   * @returns Overlay
   */
  public create(overlay: Overlay): Observable<Overlay> {
    if (overlay.color.startsWith('#')) {
      overlay.color = overlay.color.substr(1);
    }

    const url = this.apiUrl;
    const req = this.http.post<Overlay>(url, overlay);

    return req;
  }

  /**
   * Updates current Overlay
   * @param overlay Takes Overlay as object to create
   * @returns Overlay
   */
  public updateOverlay(overlay: Overlay): Observable<Overlay> {
    if (overlay.color.startsWith('#')) {
      overlay.color = overlay.color.substr(1);
    }

    const url = `${this.apiUrl}/${overlay.id}`;
    const req = this.http.put<Overlay>(url, overlay);

    return req;
  }

  /**
   * Removes an Overlay
   * @param overlayId Takes Overlay ID to delete
   * @returns void
   */
  public remove(overlayId: string): Observable<void> {
    const url = `${this.apiUrl}/${overlayId}`;
    const req = this.http.delete<Overlay>(url).pipe(map(() => undefined));

    return req;
  }

  /**
   * Searches for overlays
   * @param filters Takes OverlayQueryOpts as filters for request
   * @returns PagedResponse<Overlay>
   */
  public search(filters: OverlayQueryOpts): Observable<PagedResponse<Overlay>> {
    const serializedParams = this.paramSerializer.serialize(filters);
    const url = `${this.apiUrl}?${serializedParams}`;

    const req = this.http.get<Overlay[]>(url, { observe: 'response' }).pipe(map(response => ({
      data: response.body ?? [],
      count: parseInt(response.headers.get('X-RESULT-COUNT')!, 10),
    })));

    return req;
  }

  public searchPoints(overlayId: string, filters: OverlayQueryOpts): Observable<PagedResponse<OverlayPoint>> {
    const serializedParams = this.paramSerializer.serialize(filters);
    const url = `${this.apiUrl}/${overlayId}/overlay-points?${serializedParams}`;

    const req = this.http.get<OverlayPoint[]>(url, { observe: 'response' }).pipe(map(response => ({
      data: response.body ?? [],
      count: parseInt(response.headers.get('X-RESULT-COUNT')!, 10),
    })));

    return req;
  }

  /**
   * Search for select overlay for editing through its overlay id.
   * @param id Takes overlay id as the search parameter.
   * @returns <Overlay> data for page.
   */
  public getById(id: string): Observable<Overlay> {
    const url = `${this.apiUrl}/${id}`;
    const request = this.http.get<Overlay>(url);

    return request;
  }

  public getPointById(overlayId: string, id: string): Observable<OverlayPoint> {
    const url = `${this.apiUrl}/${overlayId}/overlay-points/${id}`;
    const request = this.http.get<OverlayPoint>(url);

    return request;
  }

  /**
   * Creating a new point for existing overlay.
   * @param overlayId Takes overlay id as search parameter.
   * @param overlayPoint The new data point.
   * @returns A requested response for completion.
   */
  public createPoint(overlayId: string, overlayPoint: OverlayPoint): Observable<Overlay> {
    const url = `${this.apiUrl}/${overlayId}/points`;
    const req = this.http.post<Overlay>(url, overlayPoint);

    return req;
  }

  /**
   * Remove an existing point from a selected overlay.
   * @param overlayId Takes overlay id as search parameter.
   * @param pointId The point id to be removed.
   * @returns A requested response for completion.
   */
  public removePoint(overlayId: string, pointId: string): Observable<void> {
    const url = `${this.apiUrl}/${overlayId}/points/${pointId}`;
    const req = this.http.delete(url).pipe(map(() => undefined));

    return req;
  }

  /**
   * Update an existing point from a selected overlay.
   * @param overlayId Takes overlay id as search parameter.
   * @param overlayPoint The data point with updated properties.
   * @returns A requested response for completion.
   */
  public updatePoint(overlayId: string, overlayPoint: OverlayPoint): Observable<OverlayPoint> {
    const pointId = overlayPoint.id;
    const url = `${this.apiUrl}/${overlayId}/points/${pointId}`;
    const req = this.http.put<OverlayPoint>(url, overlayPoint);

    return req;
  }


}
